import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Eye, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import { Panel } from '@/components/Panel';
import { AuditDate } from '@/components/AuditDate';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';
import { FilterSelect } from '@/components/list/FilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { SearchInput } from '@/components/list/SearchInput';
import { AddEmployeeDialog } from '@/components/members/AddEmployeeDialog';
import { IconCell } from '@/components/table/IconCell';
import { Pagination } from '@/components/table/Pagination';
import { PersonCell } from '@/components/table/PersonCell';
import { RowActionsMenu } from '@/components/table/RowActionsMenu';
import { SortableHeader } from '@/components/table/SortableHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useListControls } from '@/hooks/useListControls';
import { usePagedList } from '@/hooks/usePagedList';
import MemberModel from '@/models/entities/MemberModel';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import { buildEmployeeListRequest, DEFAULT_EMPLOYEE_FILTERS, DEFAULT_EMPLOYEE_SORT } from './employeeListQuery';
import type { ResolvablePath } from '@/models/resolve';
import { companyRoleIcon } from '@/components/roles/roleIcons';

/** Nom et email des employés (la société est celle sélectionnée) */
const RESOLVABLES: ResolvablePath[] = [['user']];

/** Employés de la société sélectionnée dans la sidebar, avec leur rôle dans la société (GET /members?company=, paginé côté API) */
export function EmployeesPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const list = useListControls({
    filters: DEFAULT_EMPLOYEE_FILTERS,
    sort: DEFAULT_EMPLOYEE_SORT,
    resetKey: company?.id,
  });
  const { filters, search, sort, page, itemsPerPage, changeFilters, changeSort } = list;

  // Filtre en référence (DIRECTOR…), envoyé à l'API par son id
  const companyRoles = useReferenceOptions('companyRoles');
  const request = useMemo(
    () =>
      buildEmployeeListRequest(company?.id ?? null, { ...filters, search }, sort, page, itemsPerPage, (reference) =>
        findReferenceId(companyRoles, reference),
      ),
    [company?.id, filters, search, sort, page, itemsPerPage, companyRoles],
  );
  const [adding, setAdding] = useState(false);
  /** Incrémenté pour recharger la page après un ajout */
  const [version, setVersion] = useState(0);
  // Rôle des employés dans la société : lus par leur id dans les listes de référence (chargées une fois par session)
  useReferences('companyRoles');
  const {
    models: members,
    totalItems,
    loading,
    error,
  } = usePagedList(MemberModel, request, version, { resolvables: RESOLVABLES });

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }

  return (
    <Panel className="overflow-hidden">
      <ListToolbar
        search={
          <SearchInput
            label="Rechercher un employé"
            value={filters.search}
            onChange={(value) => changeFilters({ search: value })}
          />
        }
      >
        <FilterSelect
          label="Filtrer par rôle dans la société"
          allLabel="Tous les rôles"
          value={filters.companyRole}
          onChange={(companyRole) => changeFilters({ companyRole })}
        >
          <ReferenceOptions name="companyRoles" />
        </FilterSelect>
        <Button onClick={() => setAdding(true)} disabled={!company}>
          <Plus className="w-4 h-4" />
          Nouvel employé
        </Button>
      </ListToolbar>

      <div className="overflow-x-auto">
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortableHeader className="px-6 py-4" label="Employé" sortKey="name" sort={sort} onSort={changeSort} />
              <SortableHeader className="px-6 py-4" label="Email" sortKey="email" sort={sort} onSort={changeSort} />
              <th className="px-6 py-4">Rôle dans la société</th>
              <SortableHeader
                className="px-6 py-4"
                label="Ajouté le"
                sortKey="createdAt"
                sort={sort}
                onSort={changeSort}
              />
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {members.map((member) => (
              <tr key={member.id} className="hover:bg-slate-50/80 transition-colors" data-testid="employee-row">
                <PersonCell
                  to={`/employees/${member.id}`}
                  firstName={member.firstName}
                  lastName={member.lastName}
                  fullName={member.fullName}
                />
                <IconCell icon={Mail}>{member.email}</IconCell>
                <IconCell icon={companyRoleIcon(member.companyRoleReference)}>{member.companyRoleLabel}</IconCell>
                <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                  <AuditDate date={member.createdAt} by={member.createdBy} />
                </td>
                <RowActionsMenu label={`Actions pour ${member.fullName}`}>
                  <DropdownMenuItem asChild>
                    <Link to={`/employees/${member.id}`}>
                      <Eye className="w-4 h-4" />
                      Voir la fiche
                    </Link>
                  </DropdownMenuItem>
                </RowActionsMenu>
              </tr>
            ))}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={!companiesLoaded || (loading && members.length === 0)}
          error={error}
          empty={!loading && members.length === 0 && 'Aucun employé ne correspond à ces critères.'}
        />
      </div>

      <Pagination {...list.pagination} totalItems={totalItems} label={['employé', 'employés']} />
      {adding && company && (
        <AddEmployeeDialog
          company={company}
          onClose={() => setAdding(false)}
          onSaved={(personName, teamError) => {
            toast.success(`${personName} a été ajouté à ${company.name}.`);
            if (teamError) toast.error(`Mais pas à l'équipe : ${teamError}`);
            setAdding(false);
            setVersion((v) => v + 1);
          }}
        />
      )}
    </Panel>
  );
}
